/**
 * Money is an integer number of fils. IQD has 1000 fils to the dinar, but in practice Iraqi
 * field-service pricing is in whole dinars and often whole thousands. We still store fils
 * because the moment you store dinars you cannot represent a 250-fil price adjustment, and
 * the day someone needs one you are doing a data migration on live invoices.
 *
 * There are no floats anywhere in this file, and there must be none anywhere near it.
 * `0.1 + 0.2 !== 0.3` is a rounding curiosity in a blog post and a lost invoice line here.
 */

export type Fils = number;

export const DINAR = 1000 as const;

export const dinars = (d: number): Fils => Math.round(d * DINAR);
export const toDinars = (f: Fils): number => f / DINAR;

/**
 * qty may be fractional (0.4 kg of gas); money may not. Round half-up at the LINE, once.
 * Rounding per-line and then summing is the convention that matches how a human writes an
 * invoice by hand, which is what the customer will check it against.
 */
export function lineTotal(unitCostFils: Fils, qty: number): Fils {
  return Math.round(unitCostFils * qty);
}

export function sum(lines: readonly Fils[]): Fils {
  return lines.reduce((a, b) => a + b, 0);
}

/** Tax is a per-company rate in basis points (500 = 5%). Most Iraqi SMEs run 0. */
export function taxOf(subtotal: Fils, rateBps: number): Fils {
  return Math.round((subtotal * rateBps) / 10_000);
}

export interface InvoiceTotals {
  subtotal_fils: Fils;
  discount_fils: Fils;
  taxable_fils: Fils;
  tax_fils: Fils;
  total_fils: Fils;
}

export function invoiceTotals(
  lineTotals: readonly Fils[],
  discountFils: Fils,
  taxRateBps: number,
): InvoiceTotals {
  const subtotal = sum(lineTotals);
  const discount = Math.min(Math.max(discountFils, 0), subtotal); // never negative, never > subtotal
  const taxable = subtotal - discount;
  const tax = taxOf(taxable, taxRateBps);
  return {
    subtotal_fils: subtotal,
    discount_fils: discount,
    taxable_fils: taxable,
    tax_fils: tax,
    total_fils: taxable + tax,
  };
}

/** Arabic-Indic-free, Western digits with thousands separators — what Iraqi invoices use. */
export function formatIQD(f: Fils, opts: { withSuffix?: boolean } = {}): string {
  const negative = f < 0;
  const abs = Math.abs(f);
  const whole = Math.floor(abs / DINAR);
  const frac = abs % DINAR;
  let s = whole.toLocaleString("en-US");
  if (frac !== 0) s += "." + String(frac).padStart(3, "0").replace(/0+$/, "");
  if (negative) s = "-" + s;
  return opts.withSuffix === false ? s : s + " د.ع";
}
